import React from 'react';
import { 
  useGetUserDashboardQuery, 
  useRefreshDashboardMutation
} from '../api/dashboardApiSlice';

import { Card } from '../../../components/ui/card';
import { showToast } from '../../../components/ui/Toast';
import { Button } from '../../../components/ui/button';
import { Spinner } from '../../../components/ui/spinner';
import { 
  ArrowPathIcon as RefreshIcon
} from '../../../components/icons';

export const UserDashboard: React.FC = () => {
  const { data, isLoading, error, refetch } = useGetUserDashboardQuery();
  const [refreshDashboard, { isLoading: isRefreshing }] = useRefreshDashboardMutation();
  
  const handleRefresh = async () => {
    try {
      await refreshDashboard().unwrap();
      await refetch();
      showToast('Dashboard actualizado correctamente', 'success');
    } catch (error) {
      console.error('Error al actualizar dashboard:', error);
      showToast('Error al actualizar dashboard', 'error');
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size="medium" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
        <p>Error al cargar los datos del panel de control</p>
      </div>
    );
  }
  
  if (!data) {
    return (
      <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
        <p>No hay datos disponibles en este momento</p>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Mi Dashboard</h1>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={isRefreshing}
        >
          {isRefreshing ? <Spinner size="small" /> : <RefreshIcon className="w-4 h-4 mr-2" />}
          Actualizar
        </Button>
      </div>
      
      {/* Bots asignados */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold mb-4">Mis Bots</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.bots.map((bot) => (
            <Card key={bot.botNumber} className="p-4">
              <div className="flex flex-col h-full">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="font-medium">{bot.botName}</h3>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium 
                    ${bot.status === 'good' ? 'bg-green-100 text-green-800' : 
                      bot.status === 'warning' ? 'bg-yellow-100 text-yellow-800' : 
                      'bg-red-100 text-red-800'}`
                  }>
                    {bot.status === 'good' ? 'Completado' : 
                     bot.status === 'warning' ? 'En Progreso' : 'Pendiente'}
                  </span>
                </div>
                
                <div className="mb-2">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-500">Configuración</span>
                    <span>{bot.configured} de {bot.total}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div 
                      className={`h-2.5 rounded-full ${
                        bot.status === 'good' ? 'bg-green-500' : 
                        bot.status === 'warning' ? 'bg-yellow-500' : 'bg-red-500'
                      }`} 
                      style={{ width: `${(bot.configured / bot.total) * 100}%` }}
                    ></div>
                  </div>
                </div>
                
                <div className="mt-auto pt-4">
                  <Button 
                    variant="outline" 
                    className="w-full" 
                    onClick={() => window.location.href = `/textos?bot=${bot.botNumber}`}
                  >
                    Configurar
                  </Button>
                </div>
              </div>
            </Card>
          ))}
          
          {data.bots.length === 0 && (
            <div className="col-span-full text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-gray-500">No tienes bots asignados</p>
            </div>
          )}
        </div>
      </div>

      {/* Actividad Reciente */}
      <div>
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4">Actividad Reciente</h2>
          {data.recentActivity.length > 0 ? (
            <div className="space-y-4">
              {data.recentActivity.map((item) => (
                <div key={item.id} className="border-b pb-3 last:border-b-0">
                  <div className="flex justify-between">
                    <span className="font-medium">{item.action}</span>
                    <span className="text-sm text-gray-500">{new Date(item.timestamp).toLocaleString()}</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-6">No hay actividad reciente</p>
          )}
        </Card>
      </div>
    </div>
  );
}; 